'use client';

import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function MarkdownLink({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) {
  const navigate = useNavigate();
  if (!href) return <a {...props}>{children}</a>;

  const isInternal = href.startsWith('/dashboard');
  const isJavascript = href.toLowerCase().startsWith('javascript:');

  if (isJavascript) return <span {...props}>{children}</span>;

  if (isInternal) {
    return (
      <a
        href={href}
        onClick={(e) => { e.preventDefault(); navigate(href); }}
        className="text-primary hover:underline cursor-pointer"
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" {...props}>
      {children}
    </a>
  );
}

interface AiTextMessageProps {
  content: string;
  isUser: boolean;
  showCursor?: boolean;
}

export function AiTextMessage({ content, isUser, showCursor }: AiTextMessageProps) {
  if (isUser) {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-gradient-to-r from-primary to-primary-dark text-primary-foreground">
          <div className="whitespace-pre-wrap break-words">{content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start mb-3">
      <div className="max-w-[90%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-overlay/70 text-foreground border border-surface">
        <div className="ai-markdown break-words">
          <Markdown remarkPlugins={[remarkGfm]} components={{ a: MarkdownLink as any }}>
            {content}
          </Markdown>
          {showCursor && (
            <span className="inline-block w-[2px] h-[1em] bg-primary dark:bg-primary/10 ml-0.5 align-text-bottom animate-[blink_1s_ease-in-out_infinite]" />
          )}
        </div>
      </div>
    </div>
  );
}

export function CodeBlock({ className, children }: { className?: string; children?: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const lang = match?.[1];
  const code = String(children).replace(/\n$/, '');

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!className) {
    return (
      <code className="bg-muted px-1.5 py-0.5 rounded text-[0.8em] font-mono">
        {children}
      </code>
    );
  }

  return (
    <div className="relative group my-2">
      <div className="flex items-center justify-between bg-muted rounded-t-lg px-3 py-1.5 text-xs text-muted-foreground">
        <span>{lang || 'code'}</span>
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-foreground"
        >
          {copied ? '✓' : 'Copy'}
        </button>
      </div>
      <pre className="bg-muted rounded-b-lg p-3 overflow-x-auto text-[0.8em] leading-relaxed !mt-0 !rounded-t-none">
        <code className={className}>{children}</code>
      </pre>
    </div>
  );
}
